import { FC, useEffect, useMemo, useRef, useState } from "react";
import ApexCharts, { ApexOptions } from "apexcharts";
import { getCSS, getCSSVariableValue } from "./_helpers";
import { application } from "../../_runtime/bootstrap";
import { HttpResponse } from "@dirootie-core/http-types";
import { sum } from "@dirootie-toolbox/arrays";

type ChartData = {
  yAxis: ApexAxisChartSeries;
  xAxis: string[];
};

type Props = {
  id: string;
  firstBarData: {
    name: string;
    propName: string;
  };
  secondBarData: {
    name: string;
    propName: string;
  };
  xAxisProperty: string;
  title: string;
  subTitle: string;
  urlEndpoint: string;
  height?: string;
};

export const FacetedChart: FC<Props> = ({
  id,
  firstBarData,
  secondBarData,
  xAxisProperty,
  title,
  subTitle,
  urlEndpoint,
  height = "275px",
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartReference = useMemo(() => chartRef, [chartRef]);
  const [isLoading, setIsLoading] = useState(true);
  const [chartData, setChartData] = useState<ChartData>({
    yAxis: [],
    xAxis: [],
  });

  const refreshMode = () => {
    if (!chartReference.current) {
      return;
    }

    const height = parseInt(getCSS(chartReference.current, "height"));

    const chart = new ApexCharts(
      chartReference.current,
      getChartOptions(height, chartData, title, subTitle)
    );
    if (chart) {
      chart.render();
    }

    return chart;
  };

  useEffect(() => {
    application
      .getHttpClient()
      .get(urlEndpoint)
      .then((x: HttpResponse) => {
        const data = {
          yAxis: [
            {
              name: firstBarData.name,
              data: x.body.map((x: any) => x[firstBarData.propName]),
              color: getCSSVariableValue("--bs-secondary"),
            },
            {
              name: secondBarData.name,
              data: x.body.map((x: any) => x[secondBarData.propName]),
              color: getCSSVariableValue("--bs-primary"),
            },
          ],
          xAxis: x.body.map((x: any) => x[xAxisProperty]),
        };

        setIsLoading(false);
        setTimeout(() => setChartData(data), 100);
      })
      .catch(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    const chart = refreshMode();

    return () => {
      if (chart) {
        chart.destroy();
      }
    };
  }, [chartData]);

  return (
    <div className="px-3 pt-3">
      {!isLoading && <div id={id} ref={chartRef} style={{ height }}></div>}
    </div>
  );
};

const getChartOptions = (
  height: number,
  chartData: ChartData,
  title: string,
  subTitle: string
): ApexOptions => {
  var primaryColor = getCSSVariableValue("--bs-primary");
  let activeSum: number = 0;
  let submittedSum: number = 0;

  if (chartData.yAxis[0].data && chartData.yAxis[1].data) {
    activeSum = sum(chartData.yAxis[0].data as number[]);
    submittedSum = sum(chartData.yAxis[1].data as number[]);
  }

  return {
    chart: {
      type: "bar",
      height: height,
      toolbar: {
        show: false,
      },
      fontFamily: "inherit",
      foreColor: primaryColor,
    },
    grid: {
      strokeDashArray: 4,
    },
    ...(title != "" && {
      title: {
        text: activeSum + submittedSum + ` ${title}`,
        style: {
          fontSize: "24px",
        },
      },
    }),
    ...(title != "" && {
      subtitle: {
        text: subTitle,
        style: {
          fontSize: "14px",
        },
      },
    }),
    plotOptions: {
      bar: {
        columnWidth: "30%",
        borderRadiusApplication: "end",
        borderRadius: 5,
      },
    },
    xaxis: {
      categories: chartData.xAxis,
    },
    yaxis: {
      forceNiceScale: true,
    },
    dataLabels: {
      enabled: false,
    },
    series: chartData.yAxis,
    legend: {
      position: "top",
      horizontalAlign: "center",
      ...(title != "" && {
        offsetY: -40,
      }),
    },
  };
};
